import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth, Hub } from 'aws-amplify';

const SecurityContext = createContext();

export const useAuth = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useAuth must be used within a SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRoles, setUserRoles] = useState({
    isAdmin: false,
    isReviewer: false
  });

  const checkAuthState = async () => {
    try {
      setLoading(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      setIsAuthenticated(true);
      
      // Extract roles from user attributes
      const attributes = currentUser.attributes || {};
      setUserRoles({
        isAdmin: attributes['custom:isAdmin'] === 'true',
        isReviewer: attributes['custom:isReviewer'] === 'true'
      });
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      setUserRoles({
        isAdmin: false,
        isReviewer: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthState();

    // Listen for auth state changes - with error handling
    let unsubscribe;
    
    try {
      if (Hub && typeof Hub.listen === 'function') {
        unsubscribe = Hub.listen('auth', ({ payload: { event, data } }) => {
          switch (event) {
            case 'signIn':
            case 'cognitoHostedUI':
              checkAuthState();
              break;
            case 'signOut':
              setUser(null);
              setIsAuthenticated(false);
              setUserRoles({
                isAdmin: false,
                isReviewer: false
              });
              break;
            case 'signIn_failure':
            case 'cognitoHostedUI_failure':
              // Sign in failure - handled silently
              break;
            default:
              break;
          }
        });
      } else {
        console.warn('Hub is not available - auth state changes will not be automatically detected');
      }
    } catch (error) {
      console.error('Error setting up Hub listener:', error);
    }

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from Hub:', error);
        }
      }
    };
  }, []);

  const signOut = async () => {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshAuth = () => {
    checkAuthState();
  };

  // Role checking functions
  const hasRole = (role) => {
    switch (role) {
      case 'admin':
        return userRoles.isAdmin;
      case 'reviewer':
        return userRoles.isReviewer;
      case 'user':
        return isAuthenticated;
      default:
        return false;
    }
  };

  const hasAnyRole = (roles) => {
    return roles.some(role => hasRole(role));
  };

  const canAccessRoute = (route) => {
    if (!isAuthenticated) return false;

    if (route.startsWith('/admin')) {
      return userRoles.isAdmin;
    }
    
    if (route.startsWith('/review')) {
      return userRoles.isAdmin || userRoles.isReviewer;
    }
    
    if (route.startsWith('/user')) {
      return isAuthenticated;
    }

    return true; // Public routes
  };

  const canEditContent = () => {
    return userRoles.isAdmin;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    userRoles,
    hasRole,
    hasAnyRole,
    canAccessRoute,
    canEditContent,
    signOut,
    refreshAuth
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;
